import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import { TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import Api from 'services/api'
import { color } from 'styles/colors'

type HehmFreeTrial = {
  deviceId: string
  createdAt: string
  updatedAt: string
  updatedBy: string
}

type HehmFreeTrialWithDealer = {
  deviceId: string
  createdAt: string
  updatedAt: string
  updatedBy: string
  dealerId: number | null
  customerId: number | null
}

const AssignFreeHehmTrial = () => {
  const [deviceId, setDeviceId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingTrials, setIsLoadingTrials] = useState(true)
  const [isLoadingAssignedTrials, setIsLoadingAssignedTrials] = useState(true)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [freeTrials, setFreeTrials] = useState<HehmFreeTrial[]>([])
  const [assignedTrials, setAssignedTrials] = useState<HehmFreeTrialWithDealer[]>([])

  const fetchFreeTrials = async () => {
    setIsLoadingTrials(true)
    try {
      const trials = await Api.HomeHealth.getFreeHehmTrials()
      setFreeTrials(trials || [])
    } catch (error) {
      console.error('Failed to fetch free trials:', error)
    } finally {
      setIsLoadingTrials(false)
    }
  }

  const fetchAssignedTrials = async () => {
    setIsLoadingAssignedTrials(true)
    try {
      const trials = await Api.HomeHealth.getFreeHehmTrialsWithDealers()
      const assigned = (trials || []).filter((trial: HehmFreeTrialWithDealer) => trial.dealerId !== null)
      setAssignedTrials(assigned)
    } catch (error) {
      console.error('Failed to fetch assigned trials:', error)
    } finally {
      setIsLoadingAssignedTrials(false)
    }
  }

  useEffect(() => {
    fetchFreeTrials()
    fetchAssignedTrials()
  }, [])

  const handleSubmit = async () => {
    if (!deviceId.trim()) {
      setErrorMessage('Please enter a Device ID')
      return
    }

    setIsLoading(true)
    setSuccessMessage('')
    setErrorMessage('')

    try {
      await Api.HomeHealth.assignFreeHehm(deviceId.trim().toUpperCase())
      setSuccessMessage(`Device ${deviceId.toUpperCase()} has been successfully added to the free EHMKT trials table.`)
      setDeviceId('')
      fetchFreeTrials()
      fetchAssignedTrials()
    } catch (error) {
      setErrorMessage(`Failed to assign device to free EHMKT trial. ${error?.message || 'Please try again.'}`)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  return (
    <S.Container>
      <S.Description>
        Enter a Device ID to add it to the free EHMKT trial table.
      </S.Description>

      <S.Form>
        <S.InputWrapper>
          <TextField
            label="Device ID"
            variant="outlined"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="Enter Device ID"
            disabled={isLoading}
            fullWidth
          />
        </S.InputWrapper>

        <Button
          decoration="primary"
          text={isLoading ? 'Assigning...' : 'Assign to Free Trial'}
          onPress={handleSubmit}
        />
      </S.Form>

      {successMessage && <S.Message>{successMessage}</S.Message>}
      {errorMessage && <S.Message>{errorMessage}</S.Message>}

      <S.TableSection>
        <S.SectionTitle>Current Free Trial Devices</S.SectionTitle>
        {isLoadingTrials ? (
          <S.LoadingText>Loading...</S.LoadingText>
        ) : freeTrials.length === 0 ? (
          <S.EmptyText>No devices currently in free trial.</S.EmptyText>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Device ID</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell><strong>Updated By</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {freeTrials.map((trial) => (
                  <TableRow key={trial.deviceId}>
                    <TableCell>{trial.deviceId}</TableCell>
                    <TableCell>{formatDate(trial.createdAt)}</TableCell>
                    <TableCell>{trial.updatedBy || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </S.TableSection>

      <S.TableSection>
        <S.SectionTitle>Assigned Free Trial Devices</S.SectionTitle>
        {isLoadingAssignedTrials ? (
          <S.LoadingText>Loading...</S.LoadingText>
        ) : assignedTrials.length === 0 ? (
          <S.EmptyText>No free trial devices have been assigned to dealers yet.</S.EmptyText>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Device ID</strong></TableCell>
                  <TableCell><strong>Dealer ID</strong></TableCell>
                  <TableCell><strong>Customer ID</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell><strong>Updated By</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedTrials.map((trial) => (
                  <TableRow key={trial.deviceId}>
                    <TableCell>{trial.deviceId}</TableCell>
                    <TableCell>{trial.dealerId}</TableCell>
                    <TableCell>{trial.customerId || '-'}</TableCell>
                    <TableCell>{formatDate(trial.createdAt)}</TableCell>
                    <TableCell>{trial.updatedBy || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </S.TableSection>
    </S.Container>
  )
}

export default AssignFreeHehmTrial

const S = {
  Container: styled.div`
    padding: 20px;
    max-width: 800px;
  `,
  Description: styled.p`
    color: ${color('primaryText')};
    margin-bottom: 20px;
  `,
  Form: styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
    & * { text-transform: none !important; }
  `,
  InputWrapper: styled.div`
    width: 100%;
  `,
  Message: styled.div`
    color: ${color('primaryText')};
    padding: 12px;
    border-radius: 4px;
    margin-top: 16px;
  `,
  TableSection: styled.div`
    margin-top: 32px;
  `,
  SectionTitle: styled.h3`
    color: ${color('primaryText')};
    margin-bottom: 16px;
  `,
  LoadingText: styled.p`
    color: ${color('primaryText')};
  `,
  EmptyText: styled.p`
    color: ${color('primaryText')};
    font-style: italic;
  `
}
